import React, { useState, useCallback } from "react";
import clsx from "clsx";
import { MenuItem, MealTimeInfo } from "../redux/slices/menuItemsSlice";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

interface CategoryFilterProps {
  menuItems: readonly MenuItem[];
  mealTimes: MealTimeInfo[];
  selectedCategory: string | null;
  selectedMealTime: string | null;
  onCategoryChange: (category: string | null) => void;
  onMealTimeChange: (mealTime: string | null) => void;
}

interface CategoryHierarchy {
  name: string;
  count: number;
}

const FilterButton = React.memo(
  ({
    name,
    active,
    onClick,
  }: {
    name: string;
    active: boolean;
    onClick: () => void;
  }) => {
    return (
      <button
        onClick={onClick}
        aria-pressed={active}
        className={clsx(
          "px-4 py-3 text-sm font-semibold whitespace-nowrap uppercase transition-all",

          active
            ? "border-b-2 border-[#EF4421] text-[#EF4421]"
            : "text-gray-600 hover:text-gray-900",
        )}
      >
        {name}
      </button>
    );
  },
);

FilterButton.displayName = "FilterButton";
const CategoryFilter: React.FC<CategoryFilterProps> = ({
  menuItems,
  mealTimes,
  selectedCategory,
  selectedMealTime,
  onCategoryChange,
  onMealTimeChange,
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const checkScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const { scrollLeft, scrollWidth, clientWidth } = container;
    setShowLeftArrow(scrollLeft > 10);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
  }, []);

  const handleScrollLeft = useCallback(() => {
    containerRef.current?.scrollBy({
      left: -200,
      behavior: "smooth",
    });
  }, []);

  const handleScrollRight = useCallback(() => {
    containerRef.current?.scrollBy({
      left: 200,
      behavior: "smooth",
    });
  }, []);

  // Only valid menu items

  const availableItems = React.useMemo(() => {
    return menuItems.filter((item) => item.is_enabled && item.is_item_verified);
  }, [menuItems]);

  // Build categories

  const categoryHierarchy = React.useMemo(() => {
    const map = new Map<string, CategoryHierarchy>();
    availableItems.forEach((item) => {
      const category = item.level1_category || item.category;
      if (!category) return;
      if (!map.has(category)) {
        map.set(category, {
          name: category,

          count: 0,
        });
      }
      map.get(category)!.count++;
    });

    return Array.from(map.values());
  }, [availableItems]);

  const handleAllClick = useCallback(() => {
    onCategoryChange(null);

    onMealTimeChange(null);
  }, [onCategoryChange, onMealTimeChange]);

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    checkScroll();
    container.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);
    return () => {
      container.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, [checkScroll]);

  return (
    <div className="bg-white border-b-2 border-gray-200">
      <div className="flex items-center gap-2">
        {/* LEFT ARROW */}

        <button
          onClick={handleScrollLeft}
          disabled={!showLeftArrow}
          className={clsx(
            "p-2 flex-shrink-0",

            showLeftArrow
              ? "text-gray-700"
              : "text-gray-300 cursor-not-allowed",
          )}
        >
          <FiChevronLeft />
        </button>

        <div
          ref={containerRef}
          className="
            flex-1
            overflow-x-auto
            scroll-smooth
            scrollbar-hide
          "
        >
          <div className="flex gap-6">
            {/* ALL */}

            <FilterButton
              name="All Items"
              active={selectedCategory === null && selectedMealTime === null}
              onClick={handleAllClick}
            />

            {/* NORMAL MENU CATEGORIES */}

            {categoryHierarchy.map((category) => (
              <FilterButton
                key={category.name}
                name={category.name}
                active={selectedCategory === category.name}
                onClick={() => {
                  onCategoryChange(
                    selectedCategory === category.name ? null : category.name,
                  );

                  onMealTimeChange(null);
                }}
              />
            ))}

            {/* MEAL TIME FILTERS */}

            {mealTimes.map((meal) => (
              <FilterButton
                key={meal.mealTimeId}
                name={meal.mealTimeName}
                active={selectedMealTime === meal.mealTimeId}
                onClick={() => {
                  onMealTimeChange(
                    selectedMealTime === meal.mealTimeId
                      ? null
                      : meal.mealTimeId,
                  );

                  onCategoryChange(null);
                }}
              />
            ))}
          </div>
        </div>

        {/* RIGHT ARROW */}

        <button
          onClick={handleScrollRight}
          disabled={!showRightArrow}
          className={clsx(
            "p-2 flex-shrink-0",

            showRightArrow
              ? "text-gray-700"
              : "text-gray-300 cursor-not-allowed",
          )}
        >
          <FiChevronRight />
        </button>
      </div>
    </div>
  );
};

export default React.memo(CategoryFilter);
